import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './CanvaVideoUploadModal.module.css';

interface CanvaVideoUploadModalProps {
  isOpen: boolean;
  isUploading: boolean;
  canvaUrl: string | null;
  thumbnailUrl: string | null;
  progress: number; // 0-100
  onClose: () => void;
}

// 進捗に応じたステータスメッセージを取得
function getProgressMessage(progress: number): string {
  if (progress < 50) {
    return '動画ファイルの取得中...';
  }
  return '動画をCanvaにアップロード中...';
}

export function CanvaVideoUploadModal({
  isOpen,
  isUploading,
  canvaUrl,
  thumbnailUrl,
  progress,
  onClose,
}: CanvaVideoUploadModalProps) {
  const [showCloseButton, setShowCloseButton] = useState(false);

  // アップロード完了後、少し遅延してから閉じるボタンを表示
  useEffect(() => {
    if (!isUploading && canvaUrl) {
      const timer = setTimeout(() => {
        setShowCloseButton(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setShowCloseButton(false);
    }
  }, [isUploading, canvaUrl]);

  if (!isOpen) return null;

  // body へポータル描画する。動画エディタ等では祖先要素 (transform / backdrop-filter 等) が
  // fixed の基準になり、右パネルを除いた領域の下寄せに描画されてしまうため
  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>{isUploading ? 'Canvaにアップロード中' : 'アップロード完了'}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.content}>
          {/* サムネイル表示 */}
          {thumbnailUrl && (
            <div className={styles.thumbnailContainer}>
              <img src={thumbnailUrl} alt="" className={styles.thumbnail} />
            </div>
          )}

          {isUploading && (
            <div className={styles.uploadProgress}>
              <div className={styles.spinner}></div>
              <p>{getProgressMessage(progress)}</p>
              <p className={styles.subText}>このポップアップを閉じても、アップロードはバックグラウンドで継続されます</p>
            </div>
          )}

          {!isUploading && canvaUrl && (
            <div className={styles.summary}>
              <div className={styles.summaryBox}>
                <div className={styles.instructions}>
                  <p>
                    <strong>Canvaに動画をアップロードしました</strong>
                  </p>
                  <div className={styles.openButtonContainer}>
                    <a
                      href={canvaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.openButton}
                    >
                      <img src="/canva_logo.svg" alt="" style={{ width: '16px', height: '16px' }} />
                      Canvaで開く
                    </a>
                  </div>
                  <div className={styles.tips}>
                    <p>• Canvaの動画サムネイル画像の上にあるメニューの「移動」から「アップロード」フォルダなどに素材を移動しておくと、動画のデザイン編集ページから素材を利用しやすくなります。</p>
                    <p>• Canvaでの動画素材の表示までには、少し時間が必要な場合があります。素材が表示されない場合、しばらくお待ちください。</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isUploading && showCloseButton && (
            <div className={styles.actions}>
              <button className="btn-modal" onClick={onClose}>
                閉じる
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
